import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { LicenseTier } from "@common/types/organizationBillingPolicy";

export const organizationClientPlans = [
    FLAT_RATE_PLANS.FREE,
    FLAT_RATE_PLANS.BASIC,
    FLAT_RATE_PLANS.PLUS,
    FLAT_RATE_PLANS.ADVANCED,
] as const;

export type SelectablePlans = (typeof organizationClientPlans)[number];

/**
 * Org client plans a firm may only assign when staff have explicitly listed
 * them on the organization's `allowedPlans`. Unlike Free / Basic / Plus /
 * Advanced, a
 * null or empty `allowedPlans` does NOT grant these — see
 * {@link isRestrictedOrganizationClientPlan} and `validateOrganizationAllowedPlan`.
 */
export const RESTRICTED_ORGANIZATION_CLIENT_PLANS = [
    FLAT_RATE_PLANS.READ_ONLY,
    FLAT_RATE_PLANS.TAX,
] as const;

/**
 * Display only: org client plans shown as $0 / "Complimentary" in firm views.
 * Not a billing predicate — Read-only is still billed through `readOnlyTiers`.
 * Use {@link isUnbilledOrganizationClientPlan} for billing decisions.
 */
export const COMPLIMENTARY_ORGANIZATION_CLIENT_PLANS = [
    FLAT_RATE_PLANS.READ_ONLY,
    FLAT_RATE_PLANS.TAX,
] as const;

/**
 * Plans with no Stripe product: never billed, even when an explicit price is
 * left over from a previous plan.
 */
const UNBILLED_ORGANIZATION_CLIENT_PLANS = [FLAT_RATE_PLANS.TAX] as const;

/**
 * Plans a client can't take over billing for: they have no catalog product
 * for the client to subscribe to.
 */
const CLIENT_BILLING_TRANSFER_BLOCKED_PLANS = [FLAT_RATE_PLANS.TAX] as const;

/**
 * Plans a Tax client may be moved to. Free (and Read-only) are not offered:
 * Tax has no Stripe product, so a client leaving it must land on a paid plan.
 */
const TAX_PLAN_CHANGE_TARGET_PLANS = [
    FLAT_RATE_PLANS.BASIC,
    FLAT_RATE_PLANS.PLUS,
    FLAT_RATE_PLANS.ADVANCED,
] as const;

export function isTaxPlanChangeTargetPlan(plan: FLAT_RATE_PLANS): boolean {
    return TAX_PLAN_CHANGE_TARGET_PLANS.some((target) => target === plan);
}

const SINGLE_ENTITY_ONLY_PLANS = [
    FLAT_RATE_PLANS.FREE,
    FLAT_RATE_PLANS.BASIC,
    FLAT_RATE_PLANS.TAX,
] as const;

export const PLATFORM_ASSIGNABLE_PLANS = [
    ...organizationClientPlans,
    ...RESTRICTED_ORGANIZATION_CLIENT_PLANS,
] as const;

export type OrganizationAssignablePlan =
    (typeof PLATFORM_ASSIGNABLE_PLANS)[number];

export function isPlatformVisiblePlan(plan: FLAT_RATE_PLANS | null): boolean {
    const effectivePlan = plan ?? FLAT_RATE_PLANS.FREE;
    return PLATFORM_ASSIGNABLE_PLANS.some(
        (assignable) => assignable === effectivePlan,
    );
}

export function isRestrictedOrganizationClientPlan(
    plan: FLAT_RATE_PLANS,
): boolean {
    return RESTRICTED_ORGANIZATION_CLIENT_PLANS.some(
        (restricted) => restricted === plan,
    );
}

export function isComplimentaryOrganizationClientPlan(
    plan: FLAT_RATE_PLANS,
): boolean {
    return COMPLIMENTARY_ORGANIZATION_CLIENT_PLANS.some(
        (complimentary) => complimentary === plan,
    );
}

export function isUnbilledOrganizationClientPlan(
    plan: FLAT_RATE_PLANS | null,
): boolean {
    return UNBILLED_ORGANIZATION_CLIENT_PLANS.some(
        (unbilled) => unbilled === plan,
    );
}

export function isClientBillingTransferBlockedPlan(
    plan: FLAT_RATE_PLANS | null,
): boolean {
    return CLIENT_BILLING_TRANSFER_BLOCKED_PLANS.some(
        (blocked) => blocked === plan,
    );
}

export function isSingleEntityOnlyPlan(plan: FLAT_RATE_PLANS | null): boolean {
    return SINGLE_ENTITY_ONLY_PLANS.some(
        (singleEntityPlan) => singleEntityPlan === plan,
    );
}

interface GetOrganizationAssignablePlansOptions {
    isOrganizationOwned?: boolean;
    includeRestrictedPlans?: boolean;
}

export function getOrganizationAssignablePlans(
    allowedPlans?: FLAT_RATE_PLANS[] | null,
    options?: GetOrganizationAssignablePlansOptions,
): OrganizationAssignablePlan[] {
    const includeRestrictedPlans = options?.includeRestrictedPlans ?? true;
    const assignablePlans: OrganizationAssignablePlan[] = [];

    if (allowedPlans == null || allowedPlans.length === 0) {
        assignablePlans.push(...organizationClientPlans);
    } else {
        for (const plan of organizationClientPlans) {
            if (
                allowedPlans.includes(plan) ||
                plan === FLAT_RATE_PLANS.ADVANCED
            ) {
                assignablePlans.push(plan);
            }
        }

        if (includeRestrictedPlans) {
            for (const plan of RESTRICTED_ORGANIZATION_CLIENT_PLANS) {
                if (allowedPlans.includes(plan)) {
                    assignablePlans.push(plan);
                }
            }
        }
    }

    if (options?.isOrganizationOwned === false) {
        return assignablePlans.filter(
            (plan) => !isRestrictedOrganizationClientPlan(plan),
        );
    }

    return assignablePlans;
}

/**
 * Default read-only tier policy: a single complimentary ($0, omitted from
 * Stripe) open-ended band. Used when a policy omits `readOnlyTiers` and as the
 * migration backfill for existing revisions (D4/D5/D6).
 */
export const COMPLIMENTARY_READ_ONLY_TIERS: LicenseTier[] = [
    { upTo: null, billingPriceId: null },
];

export const ORGANIZATION_CLIENT_DISCOUNT_CODE = "KICK20";
export const AIP_PROGRAM_STRIPE_COUPON_ID = "VLGCqxhf";
